import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { createAccount, listAccounts, updateAccount } from "../api/auth.js";
import { useApiClient } from "../api/ApiClientContext.js";
import type { AccountRole, WireAccount } from "../api/types.js";
import { describeApiError } from "../domain/errors.js";
import { EmployeeCodes } from "./EmployeeCodes.js";
import "../form/form.css";
import "./accounts.css";

/** Navigation state `ForcedPasswordChange` sets so a freshly-set-up admin lands here with a nudge to give themselves a code. */
export interface AccountsPageLocationState {
  promptSetCode?: boolean;
}

const MIN_PASSWORD_LENGTH = 8;

/**
 * Admin-only accounts screen (`RequireAdmin` gates the route). Deliberately
 * small: create an account, toggle active/inactive, change role, reset a
 * forgotten password. Deactivation is the only removal path — there is no
 * delete, matching `PATCH /api/accounts/:id`'s own reasoning
 * (`apps/server/src/routes/accounts.ts`): `audit_log` references and the
 * account's own history must survive it.
 */
export function AccountsPage() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const promptSetCode = (location.state as AccountsPageLocationState | null)?.promptSetCode === true;

  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: () => listAccounts(client),
  });

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<AccountRole>("staff");
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createAccount(client, { email: newEmail, password: newPassword, role: newRole }),
    onSuccess: async () => {
      setNewEmail("");
      setNewPassword("");
      setNewRole("staff");
      setCreateError(null);
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (error) => setCreateError(describeApiError(error)),
  });

  function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setCreateError(`Geslo mora imeti vsaj ${MIN_PASSWORD_LENGTH} znakov.`);
      return;
    }
    createMutation.mutate();
  }

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <h1>Uporabniki</h1>
        <button type="button" onClick={() => navigate("/")}>
          Nazaj na seznam
        </button>
      </div>

      {promptSetCode && (
        <p className="accounts-prompt" role="status">
          Geslo je nastavljeno. Zdaj si spodaj (Kode zaposlenih) dodajte svojo kodo (PIN), da boste lahko delali na blagajni.
        </p>
      )}

      <form className="accounts-create-form" onSubmit={handleCreate}>
        <div className="form-field">
          <label htmlFor="new-account-email">E-poštni naslov</label>
          <input
            id="new-account-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            disabled={createMutation.isPending}
          />
        </div>
        <div className="form-field">
          <label htmlFor="new-account-password">Začetno geslo</label>
          <input
            id="new-account-password"
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="off"
            required
            disabled={createMutation.isPending}
          />
        </div>
        <div className="form-field">
          <label htmlFor="new-account-role">Vloga</label>
          <select
            id="new-account-role"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as AccountRole)}
            disabled={createMutation.isPending}
          >
            <option value="staff">Zaposleni</option>
            <option value="admin">Administrator</option>
          </select>
        </div>
        <button type="submit" disabled={createMutation.isPending}>
          Dodaj uporabnika
        </button>
        {createError && (
          <p className="form-error" role="alert">
            {createError}
          </p>
        )}
      </form>

      {accountsQuery.isPending && (
        <p role="status" className="accounts-status">
          Nalaganje …
        </p>
      )}
      {accountsQuery.isError && (
        <p role="alert" className="accounts-status">
          Napaka pri nalaganju uporabnikov.
        </p>
      )}
      {accountsQuery.isSuccess && (
        <table className="accounts-table">
          <thead>
            <tr>
              <th>E-pošta</th>
              <th>Vloga</th>
              <th>Stanje</th>
              <th>Zadnja prijava</th>
              <th>Dejanja</th>
            </tr>
          </thead>
          <tbody>
            {accountsQuery.data.accounts.map((account) => (
              <AccountRow key={account.id} account={account} />
            ))}
          </tbody>
        </table>
      )}

      <EmployeeCodes />
    </div>
  );
}

function AccountRow({ account }: { account: WireAccount }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [resetting, setResetting] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [rowError, setRowError] = useState<string | null>(null);

  const patchMutation = useMutation({
    mutationFn: (patch: { role?: AccountRole; active?: boolean; password?: string }) => updateAccount(client, account.id, patch),
    onSuccess: async () => {
      setRowError(null);
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (error) => setRowError(describeApiError(error)),
  });

  function handleResetSubmit(event: FormEvent) {
    event.preventDefault();
    if (resetPassword.length < MIN_PASSWORD_LENGTH) {
      setRowError(`Geslo mora imeti vsaj ${MIN_PASSWORD_LENGTH} znakov.`);
      return;
    }
    patchMutation.mutate(
      { password: resetPassword },
      {
        onSuccess: () => {
          setResetting(false);
          setResetPassword("");
        },
      },
    );
  }

  return (
    <>
      <tr>
        <td>{account.email}</td>
        <td>
          <select
            value={account.role}
            onChange={(e) => patchMutation.mutate({ role: e.target.value as AccountRole })}
            disabled={patchMutation.isPending}
            aria-label={`Vloga za ${account.email}`}
          >
            <option value="staff">Zaposleni</option>
            <option value="admin">Administrator</option>
          </select>
        </td>
        <td>{account.active ? "Aktiven" : "Neaktiven"}</td>
        <td>{account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleString("sl-SI") : "—"}</td>
        <td className="accounts-row-actions">
          <button
            type="button"
            onClick={() => patchMutation.mutate({ active: !account.active })}
            disabled={patchMutation.isPending}
          >
            {account.active ? "Deaktiviraj" : "Aktiviraj"}
          </button>
          <button type="button" onClick={() => setResetting((v) => !v)} disabled={patchMutation.isPending}>
            Ponastavi geslo
          </button>
        </td>
      </tr>
      {resetting && (
        <tr>
          <td colSpan={5}>
            <form className="accounts-reset-form" onSubmit={handleResetSubmit}>
              <label htmlFor={`reset-${account.id}`}>Novo začetno geslo za {account.email}</label>
              <input
                id={`reset-${account.id}`}
                type="text"
                autoComplete="off"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                disabled={patchMutation.isPending}
              />
              <button type="submit" disabled={patchMutation.isPending}>
                Shrani
              </button>
              <button type="button" onClick={() => setResetting(false)} disabled={patchMutation.isPending}>
                Prekliči
              </button>
            </form>
          </td>
        </tr>
      )}
      {rowError && (
        <tr>
          <td colSpan={5}>
            <p className="form-error" role="alert">
              {rowError}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
